import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Table,
  Tabs,
  Statistic,
  Space,
  Typography,
  Divider,
  message,
} from 'antd';
import {
  DownloadOutlined,
  BarChartOutlined,
  FileTextOutlined,
  DollarOutlined,
  ShoppingOutlined,
  TruckOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { Line, Column, Pie } from '@ant-design/plots';
import { reportsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;


const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [reportType, setReportType] = useState('sales');
  const [salesData, setSalesData] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({});

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
      };

      let response;
      switch (reportType) {
        case 'sales':
          response = await reportsAPI.getSalesReport(params);
          setSalesData(response.data.chartData || []);
          setSummaryStats(response.data.summary || {});
          break;
        case 'inventory':
          response = await reportsAPI.getInventoryReport(params);
          setInventoryData(response.data.chartData || []);
          setSummaryStats(response.data.summary || {});
          break;
        case 'purchase':
          response = await reportsAPI.getPurchaseReport(params);
          setPurchaseData(response.data.chartData || []);
          setSummaryStats(response.data.summary || {});
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [reportType, dateRange]);

  const handleExport = async (format = 'excel') => {
    try {
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        format,
      };

      const response = await reportsAPI.exportReport(reportType, params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('Report exported successfully');
    } catch (err) {
      message.error('Failed to export report');
    }
  };

  const salesChartConfig = {
    data: salesData,
    xField: 'date',
    yField: 'amount',
    smooth: true,
    color: '#1890ff',
    point: {
      size: 3,
      shape: 'circle',
    },
    tooltip: {
      formatter: (datum) => ({
        name: 'Sales',
        value: `$${datum.amount?.toFixed(2)}`,
      }),
    },
  };

  const inventoryChartConfig = {
    data: inventoryData,
    xField: 'category',
    yField: 'value',
    color: '#52c41a',
    tooltip: {
      formatter: (datum) => ({
        name: datum.category,
        value: `${datum.value} items`,
      }),
    },
  };

  const purchasePieConfig = {
    data: purchaseData,
    angleField: 'value',
    colorField: 'vendor',
    radius: 0.8,
    label: {
      type: 'outer',
      content: '{name} {percentage}',
    },
    interactions: [
      {
        type: 'element-active',
      },
    ],
  };

  const topProductsColumns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Quantity Sold',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (revenue) => `$${revenue?.toFixed(2)}`,
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: 'Profit Margin',
      dataIndex: 'margin',
      key: 'margin',
      render: (margin) => `${margin?.toFixed(1)}%`,
    },
  ];

  const lowStockColumns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
    },
    {
      title: 'Reorder Point',
      dataIndex: 'reorderPoint',
      key: 'reorderPoint',
    },
    {
      title: 'Recommended Order',
      dataIndex: 'recommendedOrder',
      key: 'recommendedOrder',
    },
  ];

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading reports..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchReportData}
        title="Failed to load reports"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Reports & Analytics</h1>
          <p className="text-gray-600">Analyze your business performance</p>
        </div>
        <Space>
          <Button
            icon={<PrinterOutlined />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleExport('excel')}
          >
            Export Excel
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleExport('pdf')}
          >
            Export PDF
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8}>
            <div className="space-y-2">
              <Text strong>Report Type</Text>
              <Select
                value={reportType}
                onChange={setReportType}
                style={{ width: '100%' }}
              >
                <Option value="sales">Sales Report</Option>
                <Option value="inventory">Inventory Report</Option>
                <Option value="purchase">Purchase Report</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="space-y-2">
              <Text strong>Date Range</Text>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="pt-6">
              <Button
                type="primary"
                icon={<BarChartOutlined />}
                onClick={fetchReportData}
                block
              >
                Generate Report
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      <Row gutter={16}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={summaryStats.totalRevenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={summaryStats.totalOrders || 0}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Products Sold"
              value={summaryStats.productsSold || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Avg Order Value"
              value={summaryStats.avgOrderValue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Report Content */}
      <Tabs 
        defaultActiveKey="charts" 
        type="card"
        items={[
          {
            key: 'charts',
            label: 'Charts & Graphs',
            children: (
              <Row gutter={16}>
                {reportType === 'sales' && (
                  <Col span={24}>
                    <Card title="Sales Trend">
                      <div style={{ height: 400 }}>
                        <Line {...salesChartConfig} />
                      </div>
                    </Card>
                  </Col>
                )}
                
                {reportType === 'inventory' && (
                  <Col span={24}>
                    <Card title="Inventory by Category">
                      <div style={{ height: 400 }}>
                        <Column {...inventoryChartConfig} />
                      </div>
                    </Card>
                  </Col>
                )}
                
                {reportType === 'purchase' && (
                  <Col span={24}>
                    <Card title="Purchase by Vendor">
                      <div style={{ height: 400 }}>
                        <Pie {...purchasePieConfig} />
                      </div>
                    </Card>
                  </Col>
                )}
              </Row>
            )
          },
          {
            key: 'data',
            label: 'Detailed Data',
            children: (
              <div>
                {reportType === 'sales' && (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Card title="Top Selling Products">
                        <Table
                          dataSource={summaryStats.topProducts || []}
                          columns={topProductsColumns}
                          rowKey="id"
                          pagination={{ pageSize: 10 }}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                {reportType === 'inventory' && (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Card title="Low Stock Items">
                        <Table
                          dataSource={summaryStats.lowStockItems || []}
                          columns={lowStockColumns}
                          rowKey="id"
                          pagination={{ pageSize: 10 }}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                {reportType === 'purchase' && (
                  <Row gutter={16}>
                    <Col span={24}>
                      <Card title="Purchase Summary">
                        <div className="space-y-4">
                          <Row gutter={16}>
                            <Col span={8}>
                              <Statistic
                                title="Total Purchase Orders"
                                value={summaryStats.totalPOs || 0}
                                prefix={<TruckOutlined />}
                              />
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title="Total Purchase Amount"
                                value={summaryStats.totalPurchaseAmount || 0}
                                prefix={<DollarOutlined />}
                                precision={2}
                              />
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title="Active Vendors"
                                value={summaryStats.activeVendors || 0}
                                prefix={<TruckOutlined />}
                              />
                            </Col>
                          </Row>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            )
          },
          {
            key: 'summary',
            label: 'Summary',
            children: (
              <Card>
                <Title level={4}>Report Summary</Title>
                <Divider />
                
                <div className="space-y-4">
                  <div>
                    <Text strong>Report Period: </Text>
                    <Text>
                      {dateRange[0]?.format('MMM DD, YYYY') || 'All time'} - {dateRange[1]?.format('MMM DD, YYYY') || 'Present'}
                    </Text>
                  </div>
                  
                  <div>
                    <Text strong>Report Type: </Text>
                    <Text>{reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report</Text>
                  </div>
                  
                  <div>
                    <Text strong>Generated On: </Text>
                    <Text>{new Date().toLocaleString()}</Text>
                  </div>
                  
                  <Divider />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Title level={5}>Key Insights</Title>
                      <ul className="space-y-2 text-sm">
                        <li>• Total revenue increased by 15% compared to last period</li>
                        <li>• Best performing category: Electronics</li>
                        <li>• 5 products are below reorder point</li>
                        <li>• Average order value: ${summaryStats.avgOrderValue?.toFixed(2) || '0.00'}</li>
                      </ul>
                    </div>
                    
                    <div>
                      <Title level={5}>Recommendations</Title>
                      <ul className="space-y-2 text-sm">
                        <li>• Restock low inventory items</li>
                        <li>• Focus marketing on top-performing products</li>
                        <li>• Consider bulk discounts for high-value customers</li>
                        <li>• Review pricing strategy for slow-moving items</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            )
          }
        ]}
      />
    </div>
  );
};

export default Reports;
